import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft,
  User,
  Briefcase,
  CreditCard,
  IndianRupee,
  Building2,
  Save,
  Loader2,
  Calendar,
  ArrowRightLeft,
  Infinity,
  Lock,
  Info,
} from "lucide-react";
import { calculateGrossSalary, formatEnumLabel } from "@/lib/utils";
import {
  validatePAN,
  validateAadhar,
  validateIFSC,
  validateContactPhone,
} from "@/lib/validations";
import { BLOOD_GROUP_OPTIONS } from "@/constants/employee";
import { toast } from "sonner";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import {
  getSalaryComponents,
  getEarningSalaryComponents,
  getEmployeeById,
  getCompanyMiscellaneousData,
  getManagerCandidates,
  getDepartmentManagers,
  updateEmployee,
  fetchCompanyLeaveConfiguration,
  revertEmployeeTermination,
} from "@/services/adminService";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/contexts/AuthContext";
import MultipleSelector from "@/components/common/MultiSelect";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getTerminationPayroll } from "@/services/payroll.service";
import ConfirmDialog from "@/components/ConfirmDialog";
import { getTemplates } from "@/services/templateService";

const EditEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [designation, setDesignation] = useState([]);
  const [saving, setSaving] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [availableComponents, setAvailableComponents] = useState({
    earnings: [],
    deductions: [],
  });
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    date_of_birth: "",
    aadhar_number: "",
    pan_number: "",
    marital_status: "",
    address: "",

    // Emergency Contact & Medical Information
    emergency_contact_name: "",
    emergency_contact_phone: "",
    blood_group: "",

    department: "",
    designation: "",
    is_director: false,
    date_of_joining: "",
    work_location: "",
    requires_daily_login: false,
    status: "",
    leaves: [],
    bank_info: {
      bank_name: "",
      account_number: "",
      ifsc_code: "",
      branch: "",
    },
    salary_structure: {},
    shift_id: "",
    shift_name: "",
    shift_timings: "",
    has_manager: false,
    manager_id: "",
    manager: "",
  });
  const { hasFeature } = useSubscription();
  const { user: currentUser } = useAuth();
  const [shiftData, setShiftData] = useState([]);
  const [companyLeaves, setCompanyLeaves] = useState([]);
  const [errors, setErrors] = useState({});
  const [leaveBalances, setLeaveBalances] = useState({});
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [exitForm, setExitForm] = useState({
    exit_type: "",
    reason: "",
    last_working_day: "",
    final_settlement_date: "",
    exit_interview_template_id: "",
    notes: "",
    disable_access: true,
  });
  const [exitInterviewTemplates, setExitInterviewTemplates] = useState([]);
  const [loadingExitInterviewTemplates, setLoadingExitInterviewTemplates] =
    useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState({
    title: "",
    message: "",
  });
  const [originalValues, setOriginalValues] = useState({
    department: "",
    designation: "",
    gross_salary: 0,
  });
  const [checkinOutEnabled, setCheckinOutEnabled] = useState(false);

  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const [overrideConfirmOpen, setOverrideConfirmOpen] = useState(false);
  const [overrideWarning, setOverrideWarning] = useState({
    error: "",
    override_count: 0,
    promotions_to_override: [],
  });
  const [pendingPromotionPayload, setPendingPromotionPayload] = useState(null);
  const [employeeUpdateData, setEmployeeUpdateData] = useState(null);
  const showDepartmentDesignation = hasFeature("department_mapping");
  // Department manager rules, to explain what a department move will do.
  const [departmentManagers, setDepartmentManagers] = useState([]);
  useEffect(() => {
    if (!showDepartmentDesignation) return;
    getDepartmentManagers()
      .then(setDepartmentManagers)
      .catch(() => setDepartmentManagers([]));
  }, [showDepartmentDesignation]);
  const [promotionEffectiveFrom, setPromotionEffectiveFrom] = useState("");
  const [workingLocations, setWorkingLocations] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);
  // Admins who exist but can't manage (deactivated, or invite not accepted) —
  // used to flag an employee whose current manager has since been deactivated.
  const [inactiveManagerIds, setInactiveManagerIds] = useState(() => new Set());
  const [loadingManagers, setLoadingManagers] = useState(false);

  useEffect(() => {
    fetchEmployee();
    fetchSalaryComponents();
    fetchCompanyMiscellaneousData();
    fetchManagerOptions();
  }, [id, currentUser?.id, currentUser?.email]);

  useEffect(() => {
    fetchLeaveConfiguratons();
  }, []);

  // The list is fetched on every visit (above); also refresh it when the admin
  // returns to this browser tab, e.g. after adding a manager in Users & Roles.
  useEffect(() => {
    const refreshOnReturn = () => {
      if (document.visibilityState === "visible") {
        fetchManagerOptions({ silent: true });
      }
    };
    document.addEventListener("visibilitychange", refreshOnReturn);
    return () => document.removeEventListener("visibilitychange", refreshOnReturn);
  }, [currentUser?.id, currentUser?.email]);

  useEffect(() => {
    if (exitDialogOpen && exitInterviewTemplates.length === 0) {
      fetchExitInterviewTemplates();
    }
  }, [exitDialogOpen]);

  const todayISO = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const fetchLeaveConfiguratons = async () => {
    try {
      const { data: res } = await fetchCompanyLeaveConfiguration();
      const list = res?.leave_config || [];

      if (Array.isArray(list)) {
        const slimList = list.map((item) => ({
          _id: item._id,
          leave_name: item.leave_name,
          is_system_generated: item.is_system_generated || false,
          total_allocated: item.accrual_days,
          carry_forwarded: item.carry_forward_days,
        }));

        setCompanyLeaves(slimList);
      }
    } catch (err) {
      console.error("Failed to fetch leave configuration", err);
    }
  };

  const leaveOptions = companyLeaves?.map((l) => ({
    label: l.leave_name,
    value: l._id,
    disabled: l.is_system_generated || false,
  }));

  const fetchCompanyMiscellaneousData = async () => {
    try {
      const response = await getCompanyMiscellaneousData();

      setDepartments(
        response.departments.map((d) => ({
          ...d,
          _id: String(d._id),
        })),
      );

      setDesignation(
        response.designations.map((d) => ({
          ...d,
          _id: String(d._id),
        })),
      );

      setShiftData(
        response.shifts.map((s) => ({
          ...s,
          _id: String(s._id),
        })),
      );
      setWorkingLocations(response.work_locations);
      setCheckinOutEnabled(response.isCheckinOutEnabled);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to load company configuration"));
      }
    }
  };

  const fetchSalaryComponents = async () => {
    try {
      const earningsRes = await getEarningSalaryComponents("earnings");
      const deductionsRes = await getSalaryComponents("deductions");
      console.log("earningsRes", earningsRes);
      console.log("deductionsRes", deductionsRes);
      const earnings = (earningsRes.components || []).filter(
        (c) => c.is_active && c.part_of_salary_structure
      );
      const deductions = (deductionsRes.components || []).filter(
        (c) => c.is_active
      );
      setAvailableComponents({
        earnings,
        deductions,
      });
    } catch (error) {
      console.error("Error fetching salary components:", error);
      toast.error("Failed to load salary components");
    }
  };

  const fetchExitInterviewTemplates = async () => {
    try {
      setLoadingExitInterviewTemplates(true);

      const response = await getTemplates({ type: "Exit Interview" });
      const templates = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      setExitInterviewTemplates(templates);
    } catch (error) {
      console.error("Error fetching exit interview templates:", error);
      setExitInterviewTemplates([]);
      toast.error(
        getErrorMessage(error, "Failed to load exit interview templates"),
      );
    } finally {
      setLoadingExitInterviewTemplates(false);
    }
  };

  const fetchManagerOptions = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoadingManagers(true);
      // Anyone with a login can manage — admins and employees. Not the
      // logged-in admin, and never the employee being edited.
      const res = await getManagerCandidates();
      const candidates = Array.isArray(res) ? res : [];
      const filteredManagers = candidates.filter((candidate) => {
        const isCurrentUser =
          String(candidate?._id || "") === String(currentUser?.id || "") ||
          String(candidate?.email || "").toLowerCase() ===
            String(currentUser?.email || "").toLowerCase();
        const isThisEmployee =
          Boolean(candidate?.employee_code) && String(candidate.employee_code) === String(id);

        return candidate?.can_manage && !isCurrentUser && !isThisEmployee;
      });

      setInactiveManagerIds(
        new Set(
          candidates
            .filter((candidate) => !candidate?.can_manage)
            .map((candidate) => String(candidate._id)),
        ),
      );

      filteredManagers.sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || ""),
      );

      setManagerOptions(filteredManagers);
    } catch (error) {
      console.error("Error fetching manager options:", error);
      if (!silent) setManagerOptions([]);
    } finally {
      if (!silent) setLoadingManagers(false);
    }
  };

  function formatDateToDDMMYYYY(dateInput) {
    if (!dateInput) return "";
    const d = new Date(dateInput);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const fetchEmployee = async () => {
    try {
      const response = await getEmployeeById(id); //axios.get(`${API}/employees/${id}`);
      const emp = response.data;
      console.log("fetchEmployee---------", emp);

      const originalGrossSalary = Array.isArray(emp.salary_structure)
        ? emp.salary_structure
            .filter((c) => c.category !== "deductions")
            .reduce((sum, c) => sum + (Number(c.amount) || 0), 0)
        : 0;

      setOriginalValues({
        department: emp.department || "",
        designation: emp.designation || "",
        gross_salary: originalGrossSalary,
      });


      setEmployee(emp);

      // Load salary components if using component-based salary
      if (emp.salary_structure && emp.salary_structure.length > 0) {
        setSelectedComponents(emp.salary_structure);
      }

      const assignedManagerId =
        emp.manager_id || emp.manager?._id || emp.assigned_manager?._id || "";
      const assignedManagerName =
        emp.manager_name ||
        emp.manager?.name ||
        emp.assigned_manager?.name ||
        (typeof emp.manager === "string" ? emp.manager : "");

      setFormData({
        name: emp.name || "",
        email: emp.email || "",
        phone: emp.phone || "",
        gender: emp.gender || "",
        date_of_birth: formatDateToDDMMYYYY(emp.date_of_birth) || "",
        aadhar_number: emp.aadhar_number || "",
        pan_number: emp.pan_number || "",
        marital_status: emp.marital_status || "",
        address: emp.address || "",

        // Emergency Contact & Medical Information.
        // These default to null in the Employee schema, so coerce to "" to keep
        // the inputs controlled.
        emergency_contact_name: emp.emergency_contact_name || "",
        emergency_contact_phone: emp.emergency_contact_phone || "",
        blood_group: emp.blood_group || "",

        department: emp.department || "",
        designation: emp.designation || "",
        is_director: emp.is_director || false,
        date_of_joining: formatDateToDDMMYYYY(emp.date_of_joining) || "",
        work_location: emp.work_location || "",
        status: emp.status || "",
        bank_info: {
          bank_name: emp.bank_info?.bank_name || "",
          account_number: emp.bank_info?.account_number || "",
          ifsc_code: emp.bank_info?.ifsc_code || "",
          branch: emp.bank_info?.branch || "",
        },
        shift_id: emp.activeShift || "",
        shift_name: emp.shift_name || "",
        shift_timings: emp.shift_timings || "",
        has_manager:
          typeof emp.has_manager === "boolean"
            ? emp.has_manager
            : Boolean(assignedManagerId || assignedManagerName),
        manager_id: assignedManagerId,
        manager: assignedManagerName,
        salary_structure: emp.salary_structure,
        requires_daily_login: emp.requires_daily_login,
        // Leave Configuration
        is_on_probation: emp.is_on_probation || false,
        // Backend returns an ISO datetime; a type="date" input needs YYYY-MM-DD,
        // so normalise it the same way as the other date fields.
        probation_end_date: formatDateToDDMMYYYY(emp.probation_end_date) || "",
        custom_casual_leave_per_month: emp.custom_casual_leave_per_month || "",
        custom_sick_leave_per_year: emp.custom_sick_leave_per_year || "",
        annual_leave_days: emp.annual_leave_days || "",
        leaves: Array.isArray(emp.leave_balance)
          ? emp.leave_balance.map((lb) => lb.leave_id)
          : [],
      });
      const unpaidLeave = companyLeaves.find((l) => l.is_system_generated);

      if (unpaidLeave) {
        setFormData((prev) => ({
          ...prev,
          leaves: Array.from(
            new Set([...(prev.leaves || []), unpaidLeave._id]),
          ),
        }));
      }

      if (Array.isArray(emp.leave_balance)) {
        const balances = {};

        emp.leave_balance.forEach((lb) => {
          if (!lb) return;
          balances[String(lb.leave_id)] = {
            total_allocated: lb.total_allocated,
            carry_forwarded: lb.carry_forwarded,
          };
          // typeof lb.total_allocated === "number" ? lb.total_allocated : null;
        });

        setLeaveBalances(balances);
      }
    } catch (error) {
      console.error("Error fetching employee:", error);
      toast.error("Failed to load employee details");
      navigate("/employees");
    } finally {
      setLoading(false);
    }
  };

  const handleComponentToggle = (component) => {
    console.log("component", component);

    const selectedComponentId = component._id || component.component_id;
    setSelectedComponents((prev) => {
      if (!Array.isArray(prev)) prev = [];

      const exists = prev.some((c) => c.component_id === selectedComponentId);

      if (exists) {
        // REMOVE ONE — safe
        return prev.filter((c) => c.component_id !== selectedComponentId);
      }

      // ADD ONE — safe
      return [
        ...prev,
        {
          _id: selectedComponentId,
          component_id: selectedComponentId,
          component_name:
            component.component_name || component.name_in_payslip || "",
          component_type: component.component_type,
          category: component.category,
          name_in_payslip: component.name_in_payslip,
          amount: 0,
          is_active: true,
        },
      ];
    });
  };

  const handleComponentAmountChange = (componentId, amount) => {
    setSelectedComponents(
      selectedComponents.map((c) =>
        c.component_id === componentId
          ? { ...c, amount: parseFloat(amount) || 0 }
          : c,
      ),
    );
  };

  const handleInputChange = (field, value, nested = null) => {
    if (nested) {
      setFormData((prev) => ({
        ...prev,
        [nested]: {
          ...prev[nested],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSalaryChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setFormData((prev) => {
      const updated = {
        ...prev,
        salary_structure: {
          ...prev.salary_structure,
          [field]: numValue,
        },
      };

      // Auto-calculate PF and ESI based on basic salary
      if (field === "basic_salary") {
        updated.salary_structure.pf_employee = Math.round(numValue * 0.12); // 12% PF
        updated.salary_structure.pf_employer = Math.round(numValue * 0.12);

        // ESI is applicable only if total salary is below 25,000
        const grossSalary = calculateGrossSalary(updated.salary_structure);
        if (grossSalary <= 25000) {
          updated.salary_structure.esi_employee = Math.round(
            grossSalary * 0.0075,
          ); // 0.75%
          updated.salary_structure.esi_employer = Math.round(
            grossSalary * 0.0325,
          ); // 3.25%
        } else {
          updated.salary_structure.esi_employee = 0;
          updated.salary_structure.esi_employer = 0;
        }

        // Auto-calculate HRA as 40% of basic (common practice)
        if (
          updated.salary_structure.hra === 0 ||
          updated.salary_structure.hra ===
            prev.salary_structure.basic_salary * 0.4
        ) {
          updated.salary_structure.hra = Math.round(numValue * 0.4);
        }
      }

      return updated;
    });
  };

  const updateEmployeeStatus = (updatedData) => {
    setFormData((prev) => ({
      ...prev,
      status: updatedData.status ?? prev.status ?? "",
      status_reason: updatedData.status_reason ?? prev.status_reason ?? "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // -----------------------------
    // PERSONAL INFO
    // -----------------------------
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    if (showDepartmentDesignation) {
      if (!formData.department?.trim())
        newErrors.department = "Department is required";
      if (!formData.designation?.trim())
        newErrors.designation = "Designation is required";
    }
    if (!formData.shift_id) {
      newErrors.shift_id = "Shift is required";
    }
    if (formData.has_manager && !formData.manager_id) {
      newErrors.manager_id = "Please select a manager";
    } else if (
      formData.has_manager &&
      inactiveManagerIds.has(String(formData.manager_id))
    ) {
      // The backend refuses this too — catch it here with a clearer message.
      newErrors.manager_id = `${formData.manager || "The current manager"} can no longer manage (deactivated or left). Choose another manager.`;
    }
    // PAN validation
    if (formData.pan_number.trim() && !validatePAN(formData.pan_number)) {
      newErrors.pan_number = "Invalid PAN number format";
    }
    // Probation status is derived by the backend from probation_end_date.
    // The frontend no longer controls or validates is_on_probation.

    // Aadhar validation
    if (
      formData.aadhar_number.trim() &&
      !validateAadhar(formData.aadhar_number)
    ) {
      newErrors.aadhar_number = "Invalid Aadhar number format";
    }

    // Emergency details are optional — only the format is checked, and only
    // when a value was actually entered.
    if (
      formData.emergency_contact_phone?.trim() &&
      !validateContactPhone(formData.emergency_contact_phone)
    ) {
      newErrors.emergency_contact_phone =
        "Invalid emergency contact phone number";
    }

    // IFSC validation
    if (
      formData.bank_info.ifsc_code.trim() &&
      !validateIFSC(formData.bank_info.ifsc_code)
    ) {
      newErrors.ifsc_code = "Invalid IFSC code format";
    }

    // -----------------------------
    // SALARY STRUCTURE VALIDATION
    // -----------------------------
    const earningsSelected = selectedComponents.filter(
      (c) => c.category !== "deductions",
    );

    // Must select at least 1 earning component
    if (earningsSelected.length === 0 && formData.status === "active") {
      newErrors.salary_structure =
        "Please select at least one earning component";

      toast.error("Please select at least one earning component");
    } else {
      // Check if any earning has empty amount
      const missingAmounts = earningsSelected.filter(
        (c) => c.amount === null || c.amount === undefined || c.amount === "",
      );

      if (missingAmounts.length > 0) {
        newErrors.salary_structure =
          "Please enter amount for all selected earnings (0 is allowed)";

        toast.error(
          "Please enter amount for all selected earnings (0 is allowed)",
        );
      }
    }

    // -----------------------------
    // Set errors and show first error toast
    // -----------------------------
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const first = Object.values(newErrors)[0];

      toast.error(first);

      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const earningsTotal = selectedComponents
        .filter((item) => item.category !== "deductions")
        .reduce((sum, component) => sum + (Number(component.amount) || 0), 0);

      const deductionsTotal = selectedComponents
        .filter((item) => item.category === "deductions")
        .reduce((sum, component) => sum + (Number(component.amount) || 0), 0);

      const leaveBalanceInput =
        Array.isArray(formData.leaves) && formData.leaves.length > 0
          ? formData.leaves
              .map((leaveId) => {
                const leave = companyLeaves.find((l) => l._id === leaveId);
                if (!leave || leave.is_system_generated) return null; // skip unpaid

                const value = leaveBalances[leaveId];
                if (value === undefined || value === null) return null;

                return {
                  leave_id: leaveId,
                  opening_balance: value || 0,
                };
              })
              .filter(Boolean)
          : [];

      const processedData = {
        ...formData,
        status: formData.status === "pending" ? "active" : formData.status,
        // Convert empty strings to null for date fields
        date_of_birth: formData.date_of_birth || null,
        date_of_joining: formData.date_of_joining || null,
        probation_end_date: formData.probation_end_date || null,
        // Convert empty strings to null for numeric fields
        custom_casual_leave_per_month: formData.custom_casual_leave_per_month
          ? parseFloat(formData.custom_casual_leave_per_month)
          : null,
        custom_sick_leave_per_year: formData.custom_sick_leave_per_year
          ? parseFloat(formData.custom_sick_leave_per_year)
          : null,
        annual_leave_days: formData.annual_leave_days
          ? parseFloat(formData.annual_leave_days)
          : null,
        salary_structure:
          selectedComponents.length > 0
            ? selectedComponents.map((item) => ({
                component_id: item.component_id,
                component_name: item.component_name || "",
                component_type: item.component_type,
                category: item.category || "",
                name_in_payslip: item.name_in_payslip,
                amount: item.amount,
                is_active: item.is_active,
              }))
            : [],
        gross_salary: earningsTotal,
        net_salary: earningsTotal - deductionsTotal,
        shift_id: formData.shift_id,
        shift_name: formData.shift_name,
        shift_timings: formData.shift_timings,
        has_manager: formData.has_manager === true,
        manager_id: formData.has_manager ? formData.manager_id || null : null,
        manager: formData.has_manager ? formData.manager || null : null,
        leaves: formData.leaves,
        leaveBalanceInput,
      };
      const departmentChanged =
        originalValues.department !== processedData.department;
      const designationChanged =
        originalValues.designation !== processedData.designation;
      const grossSalaryChanged =
        originalValues.gross_salary !== processedData.gross_salary;

      if (employee.salary_structure) {
        if (departmentChanged || designationChanged || grossSalaryChanged) {
          setConfirmData({
            title: "Confirm Employee Promotion",
            message:
              "Select the date from which these promotion changes should apply.",
          });

          setEmployeeUpdateData(processedData);

          // Default to today if not already set
          setPromotionEffectiveFrom((prev) => prev || todayISO());

          setConfirmOpen(true);
          return;
        }
      }

      await updateEmployee(processedData, id);
      toast.success("Employee updated successfully!");
      // Refresh employee data
      await fetchEmployee();
      // Optionally navigate back or stay on page
      navigate("/employees");
    } catch (error) {
      console.error("Error updating employee:", error);
      const errorMessage = getErrorMessage(error, "Failed to update employee");
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
            <Skeleton className="h-28 w-full" />
            <div className="flex justify-end gap-3">
              <Skeleton className="h-10 w-28" />
              <Skeleton className="h-10 w-40" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Employee Not Found
          </h1>
          <p className="text-gray-500 mt-2">
            The employee you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate("/employees")} className="mt-4">
            Back to Employees
          </Button>
        </div>
      </div>
    );
  }

  const selectedExitInterviewTemplate = exitInterviewTemplates.find(
    (template) =>
      String(template._id || template.id) ===
      String(exitForm.exit_interview_template_id),
  );

  const handleInitiateExit = async () => {
    try {
      if (
        !exitForm.exit_type ||
        !exitForm.reason ||
        !exitForm.last_working_day ||
        !exitForm.final_settlement_date
      ) {
        toast.error("Please complete all required fields");
        return;
      }

      const lwd = new Date(exitForm.last_working_day);
      const fnf = new Date(exitForm.final_settlement_date);
      const dojDate = employeeDojDate();

      if (dojDate && lwd < dojDate) {
        toast.error(
          "Last working day cannot be earlier than the employee's date of joining.",
        );
        return;
      }

      if (fnf < lwd) {
        toast.error(
          "Final settlement date cannot be earlier than the last working day.",
        );
        return;
      }

      await getTerminationPayroll({
        employee_id: employee._id,
        exit_type: exitForm.exit_type,
        reason: exitForm.reason,
        last_working_day: exitForm.last_working_day,
        final_settlement_date: exitForm.final_settlement_date,
        exit_interview_template_id: exitForm.exit_interview_template_id || null,
        exit_interview_template_name: selectedExitInterviewTemplate?.name || null,
        notes: exitForm.notes,
      });

      toast.success("Exit process initiated and termination payroll created");

      setExitDialogOpen(false);
      await fetchEmployee();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to initiate exit process"));
    }
  };

  // The earliest a last working day can be is the date the employee joined — an
  // exit cannot predate joining. Anything later (past, current, or future month)
  // is permitted here; the backend rejects it only if that month's payroll is
  // already finalised (locked/completed/paid).
  const employeeDojDate = () => {
    if (!employee?.date_of_joining) return null;
    const d = new Date(employee.date_of_joining);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const employeeDojISO = () => {
    const d = employeeDojDate();
    if (!d) return undefined;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleRevertExit = async () => {
    try {
      setSaving(true);

      await revertEmployeeTermination(employee?._id);

      toast.success("Exit process reverted successfully");

      setRevertConfirmOpen(false);
      await fetchEmployee();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to revert exit process"));
    } finally {
      setSaving(false);
    }
  };

  const selectedManagerExists = managerOptions.some(
    (user) => String(user._id) === String(formData.manager_id),
  );
  const managerSelectValue = selectedManagerExists ? formData.manager_id : "";
  const currentManagerInactive =
    formData.has_manager &&
    Boolean(formData.manager_id) &&
    inactiveManagerIds.has(String(formData.manager_id));

  // Moving into a department with an active manager rule assigns that manager
  // when the move takes effect (backend applies it).
  const incomingDepartmentManager = (() => {
    if (!formData.department || formData.department === originalValues.department) return null;
    const rule = departmentManagers.find((d) => d.name === formData.department)?.manager;
    if (!rule?.active || String(rule._id) === String(formData.manager_id || "")) return null;
    return rule;
  })();

  return (
    <div className="space-y-6" data-testid="edit-employee">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              Edit Employee
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Update {employee.name}'s information
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <Button
            onClick={handleSubmit}
            disabled={saving}
            data-testid="save-employee-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="personal" className="space-y-6">
        <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 bg-transparent p-0 h-auto">
          <TabsTrigger
            value="personal"
            className="relative rounded-none border-b-2 border-transparent px-4 py-3 text-sm font-medium text-muted-foreground transition-all hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-primary"
          >
            Personal Information
          </TabsTrigger>

          <TabsTrigger
            value="job"
            className="relative rounded-none border-b-2 border-transparent px-4 py-3 text-sm font-medium text-muted-foreground transition-all hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-primary"
          >
            Job Information
          </TabsTrigger>

          <TabsTrigger
            value="salary"
            className="relative rounded-none border-b-2 border-transparent px-4 py-3 text-sm font-medium text-muted-foreground transition-all hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-primary"
          >
            Salary Structure
          </TabsTrigger>
        </TabsList>
        <TabsContent value="personal" className="mt-0 space-y-6">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>Personal Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className={errors.name ? "border-red-500" : ""}
                    data-testid="name-input"
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500 mt-1">{errors.name}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className={errors.email ? "border-red-500" : ""}
                    data-testid="email-input"
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500 mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className={errors.phone ? "border-red-500" : ""}
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-500 mt-1">{errors.phone}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="gender">Gender</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(value) =>
                      handleInputChange("gender", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date_of_birth">Date of Birth</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    max={todayISO()}
                    value={formData.date_of_birth}
                    onChange={(e) =>
                      handleInputChange("date_of_birth", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="marital_status">Marital Status</Label>
                  <Select
                    value={formData.marital_status}
                    onValueChange={(value) =>
                      handleInputChange("marital_status", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                      <SelectItem value="divorced">Divorced</SelectItem>
                      <SelectItem value="widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="aadhar_number">Aadhar Number</Label>
                  <Input
                    id="aadhar_number"
                    value={formData.aadhar_number}
                    onChange={(e) =>
                      handleInputChange("aadhar_number", e.target.value)
                    }
                    placeholder="1234 5678 9012"
                    className={errors.aadhar_number ? "border-red-500" : ""}
                  />
                  {errors.aadhar_number && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.aadhar_number}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="pan_number">PAN Number</Label>
                  <Input
                    id="pan_number"
                    value={formData.pan_number}
                    onChange={(e) =>
                      handleInputChange(
                        "pan_number",
                        e.target.value.toUpperCase(),
                      )
                    }
                    placeholder="ABCDE1234F"
                    className={errors.pan_number ? "border-red-500" : ""}
                  />
                  {errors.pan_number && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.pan_number}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  rows={3}
                />
              </div>

              {/* Emergency Contact & Medical Information */}
              <Separator />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Emergency Contact & Medical Information
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="emergency_contact_name">
                    Emergency Contact Name
                  </Label>
                  <Input
                    id="emergency_contact_name"
                    value={formData.emergency_contact_name}
                    onChange={(e) =>
                      handleInputChange(
                        "emergency_contact_name",
                        e.target.value,
                      )
                    }
                    placeholder="Enter contact name"
                  />
                </div>
                <div>
                  <Label htmlFor="emergency_contact_phone">
                    Emergency Contact Phone
                  </Label>
                  <Input
                    id="emergency_contact_phone"
                    value={formData.emergency_contact_phone}
                    onChange={(e) =>
                      handleInputChange(
                        "emergency_contact_phone",
                        e.target.value,
                      )
                    }
                    placeholder="Enter contact phone"
                    className={
                      errors.emergency_contact_phone ? "border-red-500" : ""
                    }
                  />
                  {errors.emergency_contact_phone && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.emergency_contact_phone}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="blood_group">Blood Group</Label>
                  <Select
                    value={formData.blood_group}
                    onValueChange={(value) =>
                      handleInputChange("blood_group", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select blood group" />
                    </SelectTrigger>
                    <SelectContent>
                      {BLOOD_GROUP_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CreditCard className="h-5 w-5" />
                <span>Bank Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    value={formData.bank_info.bank_name}
                    onChange={(e) =>
                      handleInputChange(
                        "bank_name",
                        e.target.value,
                        "bank_info",
                      )
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="branch">Branch</Label>
                  <Input
                    id="branch"
                    value={formData.bank_info.branch}
                    onChange={(e) =>
                      handleInputChange("branch", e.target.value, "bank_info")
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="account_number">Account Number</Label>
                  <Input
                    id="account_number"
                    value={formData.bank_info.account_number}
                    onChange={(e) =>
                      handleInputChange(
                        "account_number",
                        e.target.value,
                        "bank_info",
                      )
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="ifsc_code">IFSC Code</Label>
                  <Input
                    id="ifsc_code"
                    value={formData.bank_info.ifsc_code}
                    onChange={(e) =>
                      handleInputChange(
                        "ifsc_code",
                        e.target.value.toUpperCase(),
                        "bank_info",
                      )
                    }
                    className={errors.ifsc_code ? "border-red-500" : ""}
                  />
                  {errors.ifsc_code && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.ifsc_code}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="job" className="mt-0 space-y-6">
          {/* Job Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Briefcase className="h-5 w-5" />
                <span>Job Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {showDepartmentDesignation && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="department">Department *</Label>
                    <Select
                      value={formData.department}
                      onValueChange={(value) =>
                        handleInputChange("department", value)
                      }
                    >
                      <SelectTrigger
                        className={errors.department ? "border-red-500" : ""}
                      >
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>

                      <SelectContent>
                        {departments?.map((loc, index) => (
                          <SelectItem key={index} value={loc.name}>
                            {loc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {errors.department && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.department}
                      </p>
                    )}
                    {incomingDepartmentManager && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {formData.department}'s department manager,{" "}
                        <span className="font-medium">{incomingDepartmentManager.name}</span>,
                        becomes this employee's manager when the move takes effect.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="designation">Designation *</Label>
                    <Select
                      value={formData.designation}
                      onValueChange={(value) =>
                        handleInputChange("designation", value)
                      }
                    >
                      <SelectTrigger
                        className={errors.designation ? "border-red-500" : ""}
                      >
                        <SelectValue placeholder="Select designation" />
                      </SelectTrigger>

                      <SelectContent>
                        {designation.map((item, index) => (
                          <SelectItem key={index} value={item.name}>
                            {item.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {errors.designation && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.designation}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Director Flag */}
              <div className="mt-3">
                <div
                  className={`
                  group relative flex items-start gap-4 p-4 rounded-xl border transition-all duration-200
                  ${
                    formData.is_director
                      ? "bg-indigo-50/70 border-indigo-200 dark:bg-indigo-500/10 dark:border-indigo-500/30"
                      : "bg-background border-border hover:bg-muted/40"
                  }
                `}
                >
                  {/* Checkbox */}
                  <div className="flex items-start pt-0.5">
                    <Checkbox
                      id="is_director"
                      className="size-5"
                      checked={formData.is_director}
                      onCheckedChange={(checked) =>
                        handleInputChange("is_director", Boolean(checked))
                      }
                    />
                  </div>

                  {/* Content */}
                  <label
                    htmlFor="is_director"
                    className="flex flex-col gap-1 cursor-pointer select-none"
                  >
                    <span
                      className={`text-sm font-semibold leading-none transition-colors ${
                        formData.is_director
                          ? "text-indigo-900 dark:text-indigo-300"
                          : "text-foreground"
                      }`}
                    >
                      Company Director
                    </span>

                    <span className="text-xs leading-relaxed text-muted-foreground">
                      Applies director-specific compliance rules such as{" "}
                      <span className="font-medium text-foreground/70">
                        PF, ESI, TDS exemptions, and signing authority
                      </span>
                      .
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <Label>Shift *</Label>

                <Select
                  value={formData.shift_id}
                  onValueChange={(shiftId) => {
                    const selectedShift = shiftData.find(
                      (s) => s._id === shiftId,
                    );

                    handleInputChange("shift_id", shiftId);
                    handleInputChange("shift_name", selectedShift?.name || "");
                    handleInputChange(
                      "shift_timings",
                      selectedShift
                        ? `${selectedShift.startTime} - ${selectedShift.endTime}`
                        : "",
                    );
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Shift" />
                  </SelectTrigger>

                  <SelectContent>
                    {shiftData?.length === 0 ? (
                      <SelectItem disabled value="--">
                        No shifts found
                      </SelectItem>
                    ) : (
                      shiftData.map((shift) => (
                        <SelectItem key={shift._id} value={String(shift._id)}>
                          {shift.name} ({shift.startTime} - {shift.endTime})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>

                {errors.shift_id && (
                  <p className="text-sm text-red-500 mt-1">{errors.shift_id}</p>
                )}
              </div>

              {checkinOutEnabled && (
                <div
                  className="flex items-center space-x-2 bg-orange-50/70 border-orange-200
                  dark:bg-gray-900 dark:border-gray-700 p-4 rounded-lg border"
                >
                  <Checkbox
                    id="requires_daily_login"
                    checked={formData.requires_daily_login || false}
                    onCheckedChange={(checked) =>
                      //   setFormData({ ...formData, requires_daily_login: !!checked })
                      handleInputChange("requires_daily_login", !!checked)
                    }
                  />
                  <Label
                    htmlFor="requires_daily_login"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Is Employee Required to Punch In Daily
                  </Label>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building2 className="h-5 w-5" />
                <span>Employment Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="date_of_joining">Date of Joining</Label>
                  <Input
                    id="date_of_joining"
                    type="date"
                    value={formData.date_of_joining}
                    onChange={(e) =>
                      handleInputChange("date_of_joining", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="work_location">Work Location</Label>
                  <Select
                    id="work_location"
                    value={formData.work_location}
                    onValueChange={(value) =>
                      handleInputChange("work_location", value)
                    }
                  >
                    <SelectTrigger
                      className={errors.work_location ? "border-red-500" : ""}
                    >
                      <SelectValue placeholder="Select work location" />
                    </SelectTrigger>

                    <SelectContent>
                      {workingLocations?.map((loc, index) => (
                        <SelectItem key={index} value={loc.name}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <Label>Has Manager?</Label>
                  <RadioGroup
                    value={formData.has_manager ? "yes" : "no"}
                    onValueChange={(value) => {
                      const hasManager = value === "yes";

                      handleInputChange("has_manager", hasManager);

                      if (!hasManager) {
                        handleInputChange("manager_id", "");
                        handleInputChange("manager", "");
                      }
                    }}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="has_manager_yes" />
                      <Label htmlFor="has_manager_yes" className="font-normal">
                        Yes
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="has_manager_no" />
                      <Label htmlFor="has_manager_no" className="font-normal">
                        No
                      </Label>
                    </div>
                  </RadioGroup>

                  {formData.has_manager && (
                    <div>
                      <Label htmlFor="manager_id">Select Manager</Label>
                      <Select
                        value={managerSelectValue}
                        onValueChange={(value) => {
                          const selectedManager = managerOptions.find(
                            (user) => String(user._id) === value,
                          );

                          handleInputChange("manager_id", value);
                          handleInputChange(
                            "manager",
                            selectedManager?.name || "",
                          );
                        }}
                        disabled={
                          loadingManagers || managerOptions.length === 0
                        }
                      >
                        <SelectTrigger
                          className={
                            errors.manager_id ? "border-red-500" : ""
                          }
                        >
                          <SelectValue
                            placeholder={
                              loadingManagers
                                ? "Loading managers..."
                                : managerOptions.length === 0
                                  ? "No managers available"
                                  : formData.manager && !currentManagerInactive
                                    ? formData.manager
                                    : "Select manager"
                            }
                          />
                        </SelectTrigger>

                        <SelectContent>
                          {managerOptions.map((user) => (
                            <SelectItem
                              key={user._id}
                              value={String(user._id)}
                            >
                              {user.name}
                              {user.is_admin
                                ? " · Admin"
                                : user.designation
                                  ? ` · ${user.designation}`
                                  : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {currentManagerInactive && !loadingManagers && (
                        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                          {formData.manager || "The current manager"} can no
                          longer manage (deactivated or left). Choose another
                          manager.
                        </p>
                      )}

                      {managerOptions.length === 0 && !loadingManagers && (
                        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                          <p>
                            Managers need an active login. Invite employees to
                            the employee portal, or add admins in Users & Roles.
                          </p>
                          <Button
                            type="button"
                            variant="link"
                            className="h-auto px-0 text-amber-900"
                            onClick={() => navigate("/permission")}
                          >
                            Go to Users & Roles
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Probation status is determined by the backend from this date.
                  The admin only sets the end date — no manual toggle. */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="probation_end_date">
                    Probation End Date
                  </Label>
                  <Input
                    id="probation_end_date"
                    type="date"
                    value={formData.probation_end_date || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        probation_end_date: e.target.value,
                      })
                    }
                  />
                  {errors.probation_end_date && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.probation_end_date}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Probation is active until this date; status is set
                    automatically.
                  </p>
                </div>
              </div>
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-4">Employment Status</h3>

                <div className="flex items-center justify-between p-4 rounded-lg border bg-gray-50 dark:bg-gray-900 dark:border-gray-700">
                  <div>
                    <p className="text-xs text-gray-500">Current Status</p>
                    <p className="font-semibold text-sm">
                      {formatEnumLabel(formData.status) || "Unknown"}
                    </p>
                  </div>

                  {["exit_scheduled", "terminated", "resigned"].includes(
                    formData.status,
                  ) ? (
                    <Button
                      variant="outline"
                      onClick={() => setRevertConfirmOpen(true)}
                    >
                      Revert Exit Process
                    </Button>
                  ) : (
                    <Button
                      variant="destructive"
                      onClick={() => setExitDialogOpen(true)}
                    >
                      Initiate Exit Process
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="h-5 w-5" />
                <span>Leave Configuration</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Label>Assign Leaves *</Label>

                <MultipleSelector
                  value={formData.leaves}
                  onChange={(selectedIds) => {
                    // 1️⃣ Identify system-generated UNPAID leave explicitly
                    const unpaidLeave = companyLeaves.find(
                      (l) => l.is_system_generated === true,
                    );

                    // 2️⃣ Force unpaid leave selection
                    const finalLeaves = unpaidLeave
                      ? Array.from(
                          new Set([
                            ...selectedIds.map(String),
                            String(unpaidLeave._id),
                          ]),
                        )
                      : selectedIds.map(String);

                    // 3️⃣ Update form state
                    setFormData((prev) => ({
                      ...prev,
                      leaves: finalLeaves,
                    }));

                    // 4️⃣ Sync leave balances
                    setLeaveBalances((prev) => {
                      const next = {};

                      finalLeaves.forEach((leaveId) => {
                        const companyLeave = companyLeaves.find(
                          (l) => String(l._id) === leaveId,
                        );

                        // Skip system-generated leaves
                        if (!companyLeave || companyLeave.is_system_generated)
                          return;

                        // Preserve existing balance if present
                        const existing = employee?.leave_balance?.find(
                          (lb) => String(lb.leave_id) === leaveId,
                        );

                        next[leaveId] = {
                          total_allocated:
                            typeof existing?.total_allocated === "number"
                              ? existing.total_allocated
                              : 0,
                          carry_forwarded:
                            typeof existing?.carry_forwarded === "number"
                              ? existing.carry_forwarded
                              : 0,
                        };
                      });

                      return next;
                    });
                  }}
                  options={leaveOptions}
                  placeholder="Select Leaves"
                />

                {/* Editable leave balances */}
                {formData.leaves?.length > 0 && (
                  <div className="mt-2 space-y-2 p-4 rounded-xl border bg-gray-50 dark:bg-gray-900 dark:border-gray-700">
                    <h4 className="font-semibold text-sm mb-2">
                      Leave Balances
                    </h4>

                    <div className="space-y-3">
                      {formData.leaves.map((leaveId) => {
                        const companyLeave = companyLeaves.find(
                          (l) => l._id === leaveId,
                        );
                        if (!companyLeave) return null;

                        // Unpaid / system-generated -> show as unlimited, not editable
                        if (companyLeave.is_system_generated) {
                          return (
                            <div
                              key={leaveId}
                              className="flex items-center justify-between px-4 py-3 rounded-xl border border-dashed dark:border-slate-700 transition-all opacity-80"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0">
                                  <Infinity className="w-4 h-4 text-slate-400" />
                                </div>
                                <span className="text-sm font-bold text-slate-500 dark:text-slate-400 truncate">
                                  {companyLeave.leave_name}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 px-3 py-1 rounded-lg">
                                <Lock className="w-3 h-3 text-slate-400" />
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">
                                  Unlimited / Unpaid
                                </span>
                              </div>
                            </div>
                          );
                        }

                        // Standard Editable Balance
                        return (
                          <div
                            key={leaveId}
                            className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-4 py-4 rounded-xl border shadow-sm dark:border-slate-700 transition-all hover:border-indigo-200 dark:hover:border-indigo-900"
                          >
                            <div className="flex items-center gap-4 min-w-0">
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
                                  {companyLeave.leave_name}
                                </p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
                                  Active Allocation
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-4 self-end sm:self-center">
                              {/* Primary Balance Input */}
                              <div className="flex flex-col items-end">
                                <div className="relative flex items-center gap-2">
                                  <Input
                                    type="number"
                                    className="w-20 sm:w-24 text-center font-black text-indigo-600 dark:text-indigo-400 [&::-webkit-inner-spin-button]:appearance-none bg-slate-50/50 dark:bg-slate-900 h-9 text-base"
                                    value={
                                      leaveBalances[leaveId]?.total_allocated ||
                                      companyLeave[leaveId]?.total_allocated ||
                                      0
                                    }
                                    min={0}
                                    max={
                                      leaveBalances[leaveId]?.total_allocated
                                    }
                                    onWheel={(e) => e.currentTarget.blur()}
                                    onChange={(e) => {
                                      const value = Number(e.target.value) || 0;

                                      setLeaveBalances((prev) => ({
                                        ...prev,
                                        [leaveId]: {
                                          ...prev[leaveId],
                                          total_allocated: value,
                                        },
                                      }));
                                    }}
                                  />

                                  <div className="absolute -top-6 right-0">
                                    <span className="text-[8px] font-black text-indigo-400 uppercase tracking-widest">
                                      Total
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Carry Forward Input (Read-only logic preserved) */}
                              {companyLeave.carry_forwarded > 0 && (
                                <div className="flex items-center gap-3 border-l dark:border-slate-700 pl-4">
                                  <ArrowRightLeft className="w-3.5 h-3.5 text-slate-300 sm:block" />
                                  <div className="flex flex-col items-end">
                                    <div className="relative flex items-center gap-2">
                                      <Input
                                        type="number"
                                        className="w-20 sm:w-24 text-center [&::-webkit-inner-spin-button]:appearance-none font-bold h-9"
                                        min={0}
                                        max={
                                          leaveBalances[leaveId].total_allocated
                                        }
                                        value={
                                          leaveBalances[leaveId].carry_forwarded
                                        }
                                        onWheel={(e) => e.currentTarget.blur()}
                                        onChange={(e) => {
                                          const raw = Number(e.target.value);
                                          const max =
                                            leaveBalances[leaveId]
                                              .total_allocated;

                                          const value = Number.isNaN(raw)
                                            ? 0
                                            : Math.min(Math.max(raw, 0), max);

                                          setLeaveBalances((prev) => ({
                                            ...prev,
                                            [leaveId]: {
                                              ...prev[leaveId],
                                              carry_forwarded: value,
                                            },
                                          }));
                                        }}
                                      />

                                      <div className="absolute -top-6 right-0">
                                        <span className="text-[8px] font-black text-indigo-400 uppercase tracking-widest">
                                          Carried
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <div className="flex items-center space-x-2 bg-blue-50 dark:bg-blue-500/30 p-2 rounded-lg border border-blue-200">
                        <Info className="w-4 h-4 text-blue-500" />
                        <Label className="text-xs cursor-pointer">
                          Total leave balance includes both allocated and
                          carried-forward leaves.
                        </Label>
                      </div>
                    </div>
                  </div>
                )}

                {errors.leaves && (
                  <p className="text-sm text-red-500 mt-1">{errors.leaves}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="salary" className="mt-0">
          {/* Salary Structure */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <IndianRupee className="h-5 w-5" />
                <span>Salary Structure (Component-Based)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Migration Notice for Fixed Salary Employees */}
              {employee?.salary_structure && !employee?.salary_structure && (
                <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 16v-4"></path>
                      <path d="M12 8h.01"></path>
                    </svg>
                    <div>
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                        This employee uses legacy fixed salary structure
                      </h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        To update this employee's salary, select components
                        below. The new component-based system provides better
                        flexibility and reporting.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Component-Based Salary Structure */}
              <div className="space-y-6">
                {/* Earnings Components */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-primary">
                      Earnings
                    </h3>
                    <Select
                      value=""
                      onValueChange={(value) => {
                        console.log("availableComponents", value);

                        const component = availableComponents.earnings.find(
                          (c) => c._id === value,
                        );
                        if (component) {
                          handleComponentToggle(component);
                        }
                      }}
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="+ Add Earning" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableComponents.earnings &&
                        availableComponents.earnings.length > 0 ? (
                          <>
                            {availableComponents.earnings
                              .filter(
                                (c) =>
                                  !selectedComponents.find(
                                    (sc) => sc.component_id === c._id,
                                  ),
                              )
                              .map((component) => (
                                <SelectItem
                                  key={component._id}
                                  value={component._id}
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium">
                                      {component.name_in_payslip}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {component.component_type}
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                            {availableComponents.earnings.filter(
                              (c) =>
                                !selectedComponents.find(
                                  (sc) => sc.component_id === c._id,
                                ),
                            ).length === 0 && (
                              <SelectItem value="placeholder" disabled>
                                All components added
                              </SelectItem>
                            )}
                          </>
                        ) : (
                          <SelectItem value="placeholder" disabled>
                            No components available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Selected Earnings */}
                  <div className="space-y-2">
                    {selectedComponents.filter(
                      (c) => c.category !== "deductions",
                    ).length === 0 ? (
                      <div className="text-center py-12 border-2 border-dashed rounded-lg bg-gray-50/50 dark:bg-gray-800/50">
                        <p className="text-gray-500 dark:text-gray-400">
                          No earnings added yet
                        </p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                          Click "Add Earning" to get started
                        </p>
                      </div>
                    ) : (
                      selectedComponents
                        .filter((c) => c.category !== "deductions")
                        .map((component) => (
                          <div
                            key={component._id}
                            className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 border rounded-lg hover:shadow-md transition-shadow"
                          >
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {component.name_in_payslip}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {component.category}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center">
                                <span className="text-gray-500 mr-2">₹</span>
                                <Input
                                  type="number"
                                  onWheel={(e) => e.target.blur()}
                                  placeholder="0"
                                  className="w-32 text-right"
                                  value={component.amount || ""}
                                  onChange={(e) =>
                                    handleComponentAmountChange(
                                      component.component_id,
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                onClick={() => handleComponentToggle(component)}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M3 6h18"></path>
                                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                </svg>
                              </Button>
                            </div>
                          </div>
                        ))
                    )}
                  </div>

                  {/* Earnings Total */}
                  {selectedComponents.length > 0 && (
                    <div className="flex justify-end items-center gap-2 px-4 py-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        Total Earnings:
                      </span>
                      <span className="text-xl font-bold text-green-600 dark:text-green-400">
                        ₹
                        {selectedComponents
                          .filter((c) => c.category !== "deductions")
                          .reduce(
                            (sum, c) => sum + (parseFloat(c.amount) || 0),
                            0,
                          )
                          .toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Deductions Components */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-red-600">
                      Deductions
                    </h3>
                    <Select
                      value=""
                      onValueChange={(value) => {
                        const component = availableComponents.deductions.find(
                          (c) => c._id === value,
                        );
                        if (component) {
                          handleComponentToggle(component);
                        }
                      }}
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="+ Add Deduction" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableComponents.deductions &&
                        availableComponents.deductions.length > 0 ? (
                          <>
                            {availableComponents.deductions
                              .filter(
                                (c) =>
                                  !selectedComponents.find(
                                    (sc) => sc.component_id === c._id,
                                  ),
                              )
                              .map((component) => (
                                <SelectItem
                                  key={component._id}
                                  value={component._id}
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium">
                                      {component.name_in_payslip}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {component.component_type}
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                            {availableComponents.deductions.filter(
                              (c) =>
                                !selectedComponents.find(
                                  (sc) => sc.component_id === c._id,
                                ),
                            ).length === 0 && (
                              <SelectItem value="placeholder" disabled>
                                All components added
                              </SelectItem>
                            )}
                          </>
                        ) : (
                          <SelectItem value="placeholder" disabled>
                            No components available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Selected Deductions */}
                  <div className="space-y-2">
                    {selectedComponents.filter(
                      (c) => c.category === "deductions",
                    ).length === 0 ? (
                      <div className="text-center py-12 border-2 border-dashed rounded-lg bg-gray-50/50 dark:bg-gray-800/50">
                        <p className="text-gray-500 dark:text-gray-400">
                          No deductions added yet
                        </p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                          Click "Add Deduction" to get started
                        </p>
                      </div>
                    ) : (
                      selectedComponents
                        .filter((c) => c.category === "deductions")
                        .map((component) => (
                          <div
                            key={component._id}
                            className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 border rounded-lg hover:shadow-md transition-shadow"
                          >
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {component.name_in_payslip}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {component.category}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center">
                                <span className="text-gray-500 mr-2">₹</span>
                                <Input
                                  type="number"
                                  onWheel={(e) => e.target.blur()}
                                  placeholder="0"
                                  className="w-32 text-right"
                                  value={component.amount || ""}
                                  onChange={(e) =>
                                    handleComponentAmountChange(
                                      component.component_id,
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                onClick={() => handleComponentToggle(component)}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M3 6h18"></path>
                                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                </svg>
                              </Button>
                            </div>
                          </div>
                        ))
                    )}
                  </div>

                  {/* Deductions Total */}
                  {selectedComponents.filter((c) => c.category === "deductions")
                    .length > 0 && (
                    <div className="flex justify-end items-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        Total Deductions:
                      </span>
                      <span className="text-xl font-bold text-red-600 dark:text-red-400">
                        ₹
                        {selectedComponents
                          .filter((c) => c.category === "deductions")
                          .reduce(
                            (sum, c) => sum + (parseFloat(c.amount) || 0),
                            0,
                          )
                          .toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Salary Summary */}
                <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-lg border">
                  <h4 className="font-semibold mb-4 text-lg">Salary Summary</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">
                        Gross Salary:
                      </span>
                      <span className="font-semibold text-lg">
                        ₹
                        {selectedComponents
                          .filter((c) => c.category !== "deductions")
                          .reduce(
                            (sum, c) => sum + (parseFloat(c.amount) || 0),
                            0,
                          )
                          .toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">
                        Total Deductions:
                      </span>
                      <span className="font-semibold text-lg text-red-600 dark:text-red-400">
                        -₹
                        {selectedComponents
                          .filter((c) => c.category === "deductions")
                          .reduce(
                            (sum, c) => sum + (parseFloat(c.amount) || 0),
                            0,
                          )
                          .toLocaleString()}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center pt-2">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        Net Salary:
                      </span>
                      <span className="text-2xl font-bold text-primary">
                        ₹
                        {(
                          selectedComponents
                            .filter((c) => c.category !== "deductions")
                            .reduce(
                              (sum, c) => sum + (parseFloat(c.amount) || 0),
                              0,
                            ) -
                          selectedComponents
                            .filter((c) => c.category === "deductions")
                            .reduce(
                              (sum, c) => sum + (parseFloat(c.amount) || 0),
                              0,
                            )
                        ).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {exitDialogOpen && (
        <Dialog open={exitDialogOpen} onOpenChange={setExitDialogOpen}>
          <DialogContent className="flex max-h-[90vh] max-w-xl flex-col gap-0 overflow-hidden p-0">
            <DialogHeader className="shrink-0 border-b bg-muted/50 px-6 py-4 text-left">
              <DialogTitle>
                Initiate Exit Process
              </DialogTitle>
              <DialogDescription>
                Record exit details and generate final settlement payroll.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 space-y-4 overflow-y-auto p-6">
              <div className="rounded-lg border bg-amber-50 p-3 dark:bg-amber-900/30">
                <p className="text-sm text-amber-700 dark:text-amber-200">
                  This will mark the employee for separation and create a
                  termination payroll run. Review carefully.
                </p>
              </div>

              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Exit Type *</Label>
                  <Select
                    value={exitForm.exit_type}
                    onValueChange={(v) =>
                      setExitForm((p) => ({ ...p, exit_type: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="terminated_by_employer">
                        Terminated By Employer
                      </SelectItem>
                      <SelectItem value="termination_by_death">
                        Termination By Death
                      </SelectItem>
                      <SelectItem value="termination_by_disability">
                        Termination By Disability
                      </SelectItem>
                      <SelectItem value="resigned_by_employee">
                        Resigned By Employee
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Reason *</Label>
                  <Textarea
                    rows={3}
                    value={exitForm.reason}
                    onChange={(e) =>
                      setExitForm((p) => ({ ...p, reason: e.target.value }))
                    }
                    placeholder="Brief explanation of exit reason"
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Last Working Day *</Label>

                  <Input
                    type="date"
                    value={exitForm.last_working_day}
                    min={employeeDojISO()}
                    onChange={(e) =>
                      setExitForm((p) => ({
                        ...p,
                        last_working_day: e.target.value,
                      }))
                    }
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (!value) return;

                      const selected = new Date(value);
                      const minDate = employeeDojDate();

                      if (minDate && selected < minDate) {
                        toast.error(
                          "Last working day cannot be before the employee's date of joining.",
                        );

                        setExitForm((p) => ({ ...p, last_working_day: "" }));
                      }
                    }}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Final Settlement Date *</Label>

                  <Input
                    type="date"
                    value={exitForm.final_settlement_date}
                    onChange={(e) =>
                      setExitForm((p) => ({
                        ...p,
                        final_settlement_date: e.target.value,
                      }))
                    }
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (!value) return;

                      if (exitForm.last_working_day) {
                        const selected = new Date(value);
                        const lwd = new Date(exitForm.last_working_day);

                        if (selected < lwd) {
                          toast.error(
                            "Final settlement date cannot be before the last working day.",
                          );

                          setExitForm((p) => ({
                            ...p,
                            final_settlement_date: "",
                          }));
                        }
                      }
                    }}
                  />
                </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Exit Interview</Label>
                  <Select
                    value={exitForm.exit_interview_template_id || "none"}
                    disabled={loadingExitInterviewTemplates}
                    onValueChange={(value) =>
                      setExitForm((p) => ({
                        ...p,
                        exit_interview_template_id:
                          value === "none" ? "" : value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          loadingExitInterviewTemplates
                            ? "Loading templates..."
                            : "Select exit interview template"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No exit interview</SelectItem>
                      {exitInterviewTemplates.map((template) => {
                        const templateId = String(template._id || template.id);
                        const questionCount = (template.sections || []).reduce(
                          (sum, section) =>
                            sum + (section.questions?.length || 0),
                          0,
                        );

                        return (
                          <SelectItem key={templateId} value={templateId}>
                            {template.name}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {!loadingExitInterviewTemplates &&
                    exitInterviewTemplates.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        No exit interview templates found.
                      </p>
                    )}
                </div>

                <div className="space-y-1.5">
                  <Label>Internal Notes (optional)</Label>
                  <Textarea
                    rows={2}
                    value={exitForm.notes}
                    onChange={(e) =>
                      setExitForm((p) => ({ ...p, notes: e.target.value }))
                    }
                    placeholder="Any HR comments or settlement notes"
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="shrink-0 border-t bg-muted/50 px-6 py-4">
              <Button
                variant="outline"
                onClick={() => setExitDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleInitiateExit}>
                Confirm & Create Final Payroll
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{confirmData.title}</DialogTitle>
            <DialogDescription>{confirmData.message}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>Promotion Effective From *</Label>
              <Input
                type="date"
                value={promotionEffectiveFrom}
                onChange={(e) => setPromotionEffectiveFrom(e.target.value)}
              />
              {!promotionEffectiveFrom ? (
                <p className="text-xs text-red-500 mt-1">
                  Please select a date.
                </p>
              ) : null}
            </div>

            <div className="text-xs text-muted-foreground">
              This date will be stored with the promotion change for audits /
              payroll impact.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>

            <Button
              disabled={saving || !promotionEffectiveFrom}
              onClick={async () => {
                if (!promotionEffectiveFrom) {
                  toast.error("Please select promotion effective date");
                  return;
                }

                // Attach effective date to the payload you already prepared
                const payload = {
                  ...employeeUpdateData,
                  promotion_effective_from: promotionEffectiveFrom,
                };

                try {
                  setSaving(true);

                  await updateEmployee(payload, id);
                  toast.success("Employee updated successfully!");
                  setConfirmOpen(false);
                  navigate("/employees");
                } catch (error) {
                  const warning = error?.response?.data;
                  const requiresConfirmation =
                    error?.response?.status === 409 &&
                    warning?.requires_confirmation;

                  if (requiresConfirmation) {
                    setOverrideWarning({
                      error:
                        warning?.error ||
                        "This backdated promotion will override future promotions.",
                      override_count: Number(warning?.override_count || 0),
                      promotions_to_override: Array.isArray(
                        warning?.promotions_to_override,
                      )
                        ? warning.promotions_to_override
                        : [],
                    });
                    setPendingPromotionPayload(payload);
                    setConfirmOpen(false);
                    setOverrideConfirmOpen(true);
                    return;
                  }

                  toast.error(
                    getErrorMessage(error, "Failed to update employee"),
                  );
                } finally {
                  setSaving(false);
                }
              }}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                "Confirm Promotion"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={overrideConfirmOpen}
        onOpenChange={(open) => {
          setOverrideConfirmOpen(open);
          if (!open && pendingPromotionPayload) {
            setConfirmOpen(true);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Override Future Promotions?</DialogTitle>
            <DialogDescription>
              {overrideWarning.error ||
                "This backdated promotion will override future promotions."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <p className="font-medium">
              Promotions to override: {overrideWarning.override_count || 0}
            </p>

            {Array.isArray(overrideWarning.promotions_to_override) &&
            overrideWarning.promotions_to_override.length > 0 ? (
              <div className="max-h-48 overflow-y-auto rounded-md border p-3">
                <ul className="space-y-2">
                  {overrideWarning.promotions_to_override
                    .slice(0, 10)
                    .map((p) => (
                      <li key={p.id}>
                        {p?.effective_from
                          ? new Date(p.effective_from).toLocaleDateString(
                              "en-GB",
                            )
                          : "--"}
                        {" - "}
                        {p?.promotion_type || "promotion"}
                        {p?.new_gross_salary
                          ? ` - ${Number(p.new_gross_salary).toLocaleString("en-IN")}`
                          : ""}
                      </li>
                    ))}
                </ul>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOverrideConfirmOpen(false);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              disabled={saving || !pendingPromotionPayload}
              onClick={async () => {
                if (!pendingPromotionPayload) return;

                try {
                  setSaving(true);
                  await updateEmployee(
                    {
                      ...pendingPromotionPayload,
                      confirm_override_promotions: true,
                    },
                    id,
                  );
                  const count = Number(overrideWarning.override_count || 0);
                  toast.success(
                    `Employee updated successfully. ${count} future promotion${count === 1 ? "" : "s"} overridden.`,
                  );
                  setPendingPromotionPayload(null);
                  setOverrideConfirmOpen(false);
                  setConfirmOpen(false);
                  navigate("/employees");
                } catch (error) {
                  toast.error(
                    getErrorMessage(error, "Failed to update employee"),
                  );
                } finally {
                  setSaving(false);
                }
              }}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Overriding...
                </>
              ) : (
                "Yes, Override"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={revertConfirmOpen}
        onOpenChange={setRevertConfirmOpen}
        title="Revert Exit Process?"
        message="This will cancel the exit process, delete termination records, and restore the employee to active status. This action will also delete any associated final settlement payroll runs."
        destructive
        confirmText="Revert Exit"
        onConfirm={handleRevertExit}
      />
    </div>
  );
};

export default EditEmployee;
